import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SkeletonLoader } from '@yoast/ui-library';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import Initializer from './components/Initializer';
import PageTitle from './components/Layout/PageTitle';
import { AnalysisIdGuard } from './components/RouteGuards/AnalysisIdGuard';
import { BrandIdGuard } from './components/RouteGuards/BrandIdGuard';
import { ToastContainer } from './components/ToastContainer';
import ToastDemo from './components/ToastDemo';
import { BrandAnalysisProvider } from './contexts';
import { ToastProvider } from './contexts/ToastContext';
import type { HostAppProps } from './contexts/types';
import BrandAnalysis from './pages/BrandAnalysis/BrandAnalysis';

// Lazy-loaded components
const BrandList = lazy(() => import('./pages/BrandList/BrandList'));
const AnalysisDetails = lazy(() => import('./pages/AnalysisDetails/AnalysisDetails'));
const ScanDetails = lazy(() => import('./pages/ScanDetails/ScanDetails'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Setup = lazy(() => import('./pages/Setup'));
const QuerySelection = lazy(() => import('./pages/Setup/2-QuerySelection'));
const Analysis = lazy(() => import('./pages/Setup/3-Analysis'));

const AccountDebug = lazy(() => import('./pages/AccountDebug/AccountDebug'));
const OnboardingChat = lazy(() => import('./pages/OnboardingChat/OnboardingChat'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
    mutations: {
      retry: false,
    },
  },
});

// Conditional DevTools component that only loads in development
const ReactQueryDevtools = lazy(() => {
  if (import.meta.env.DEV) {
    return import('@tanstack/react-query-devtools').then((module) => ({
      default: module.ReactQueryDevtools,
    }));
  }
  // Return a dummy component for production
  return Promise.resolve({ default: () => null });
});

// Loading component for lazy-loaded routes
const LoadingFallback: React.FC = () => (
  <div className="yst-flex yst-min-h-[200px] yst-items-center yst-justify-center">
    <div className="yst-w-full yst-max-w-md yst-space-y-3">
      <SkeletonLoader className="yst-h-8 yst-w-3/4" />
      <SkeletonLoader className="yst-h-6 yst-w-full" />
      <SkeletonLoader className="yst-h-6 yst-w-2/3" />
    </div>
  </div>
);

const AppContent: React.FC<{
  hideNav?: boolean;
  disableLayout?: boolean;
  DebugInfo?: React.FC;
}> = ({ hideNav = false, disableLayout = false, DebugInfo }) => {
  return (
    <Routes>
    <Route
      element={
        // TODO: move layout out of Initializer?
        <Initializer
          hideNav={hideNav}
          disableLayout={disableLayout}
          DebugInfo={DebugInfo}
        />
      }
    >
      {/* 1. /brand/... */}
      <Route path="/" element={<Outlet />}>
        <Route
          index
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PageTitle title="AI Brand Insights" />
              <BrandAnalysis />
            </Suspense>
          }
        />

        {/* Routes that require brandId validation */}
        <Route path="brands/:brandId" element={<BrandIdGuard />}>
          <Route index element={<BrandAnalysis />} />
          <Route
            path="brand-list"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PageTitle title="Brands - AI Brand Insights" />
                <BrandList />
              </Suspense>
            }
          />

          {/* Routes that require both brandId and analysisId validation */}
          <Route path="analysis/:analysisId" element={<AnalysisIdGuard />}>
            <Route
              index
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PageTitle title="Analysis - AI Brand Insights" />
                  <BrandAnalysis />
                </Suspense>
              }
            />
            <Route
              path="scan-details"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PageTitle title="Scan details - AI Brand Insights" />
                  <ScanDetails />
                </Suspense>
              }
            />
            <Route
              path="analysis-details"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PageTitle title="Analysis Details - AI Brand Insights" />
                  <AnalysisDetails />
                </Suspense>
              }
            />
          </Route>
        </Route>

        {/* brand setup routes */}
        <Route path="/setup">
          {/* step 1: chat onboarding (standalone full-page layout, no wizard shell) */}
          <Route
            index
            element={
              <Suspense fallback={<LoadingFallback />}>
                <OnboardingChat />
              </Suspense>
            }
          />
          {/* steps 2 & 3: validate the brandId first, then render the wizard
              shell (so an invalid brand never renders the wizard chrome) */}
          <Route path=":brandId" element={<BrandIdGuard />}>
            <Route
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PageTitle title="Setup brand - AI Brand Insights" />
                  <Setup />
                </Suspense>
              }
            >
              {/* /setup/:brandId with no step → default to queries */}
              <Route index element={<Navigate to="queries" replace />} />
              <Route
                path="queries"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <PageTitle title="Setup queries - AI Brand Insights" />
                    <QuerySelection />
                  </Suspense>
                }
              />
              <Route
                path="analysis"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <PageTitle title="Analysis - AI Brand Insights" />
                    <Analysis />
                  </Suspense>
                }
              />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* 2. settings */}
      <Route
        path="/settings"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <PageTitle title="Settings - AI Brand Insights" />
            <Settings />
          </Suspense>
        }
      />

      {/* 3. account debug */}
      <Route
        path="/account-debug"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <PageTitle title="Account debug - AI Brand Insights" />
            <AccountDebug />
          </Suspense>
        }
      />
      {import.meta.env.DEV && (
        <Route path="/toast-test" element={<ToastDemo />} />
      )}
    </Route>
  </Routes>
  );
};

const App = ({
  DebugInfo,
  hostAppProps,
}: {
  DebugInfo?: React.FC;
  hostAppProps?: HostAppProps;
}) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrandAnalysisProvider hostAppProps={hostAppProps}>
          <AppContent DebugInfo={DebugInfo} />
          <ToastContainer />
        </BrandAnalysisProvider>
      </ToastProvider>
      <Suspense fallback={null}>
        <ReactQueryDevtools initialIsOpen={false} />
      </Suspense>
    </QueryClientProvider>
  );
};

export const AppWithoutLayout = () => (
  <QueryClientProvider client={queryClient}>
    <ToastProvider>
      <BrandAnalysisProvider>
        <AppContent disableLayout />
        <ToastContainer />
      </BrandAnalysisProvider>
    </ToastProvider>
    <Suspense fallback={null}>
      <ReactQueryDevtools initialIsOpen={false} />
    </Suspense>
  </QueryClientProvider>
);

export default App;
